import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useInvoices } from '../hooks/useInvoices';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { InvoiceGenerator } from '../components/invoice/InvoiceGenerator';
import { EmailInvoice } from '../components/email/EmailInvoice';
import { SalesReports } from '../components/reports/SalesReports';
import { useOfflineGuard } from '../components/ui/OfflineBanner';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Mail,
  Eye,
  Edit,
  X,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  DollarSign,
  Calendar,
  BarChart3,
  Download,
  WifiOff
} from 'lucide-react';

export const SalesInvoices: React.FC = () => {
  const { user } = useAuth();
  const { isNetworkOffline } = useOfflineGuard();
  const { language } = useLanguage();
  const { 
    invoices, 
    loading, 
    updateInvoiceStatus, 
    cancelInvoice, 
    generateSalesReport,
    exportInvoicesCSV 
  } = useInvoices();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'unpaid' | 'cancelled'>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'invoices' | 'reports'>('invoices');

  const hasPricingAccess = user?.pricing_enabled || user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'worker';

  if (!hasPricingAccess) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center">
            <CreditCard className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-300">
                Pricing System Required
              </h3>
              <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                The invoice and payment system requires pricing access. Please contact your administrator to enable pricing features.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.payment_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (invoiceId: string, newStatus: any) => {
    if (isNetworkOffline) {
      alert('Cannot update invoice status while offline');
      return;
    }
    const success = await updateInvoiceStatus(invoiceId, newStatus);
    if (success) {
      alert('Invoice status updated successfully!');
    } else {
      alert('Failed to update invoice status');
    }
  };

  const handleCancelInvoice = async (invoiceId: string) => {
    if (isNetworkOffline) {
      alert('Cannot cancel invoice while offline');
      return;
    }
    if (confirm('Are you sure you want to cancel this invoice? This will restore inventory quantities.')) {
      const success = await cancelInvoice(invoiceId);
      if (success) {
        alert('Invoice cancelled successfully! Inventory quantities have been restored.');
      } else {
        alert('Failed to cancel invoice');
      }
    }
  };

  const handleEmailInvoice = async (email: string, subject: string, message: string) => {
    try {
      // Simulate email sending
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update invoice email_sent_at timestamp
      console.log('Email sent successfully to:', email);
      return true;
    } catch (error) {
      console.error('Failed to send email:', error);
      return false;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'unpaid': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'overdue': return <Clock className="h-4 w-4 text-red-500" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-gray-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'unpaid': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'overdue': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'cash': return '💰';
      case 'pin': return '💳';
      case 'lowan': return '📋';
      case 'bank_transfer': return '🏦';
      default: return '💳';
    }
  };

  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.payment_status === 'paid').length,
    unpaid: invoices.filter(i => i.payment_status === 'unpaid').length,
    cancelled: invoices.filter(i => i.payment_status === 'cancelled').length,
    totalRevenue: invoices
      .filter(i => i.payment_status === 'paid')
      .reduce((sum, i) => sum + i.total_amount, 0),
    pendingRevenue: invoices
      .filter(i => i.payment_status === 'unpaid')
      .reduce((sum, i) => sum + i.total_amount, 0)
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Sales & Invoices
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage invoices, track payments, and view sales reports
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <FileText className="h-4 w-4 mr-2 inline" />
              Invoices
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'reports'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BarChart3 className="h-4 w-4 mr-2 inline" />
              Reports & Analytics
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'invoices' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
            <div className="card p-6">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Invoices</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">✅ Paid</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.unpaid}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">⏳ Unpaid</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-gray-600">{stats.cancelled}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">🚫 Cancelled</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-green-600">€{stats.totalRevenue.toFixed(2)}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">💰 Revenue</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-orange-600">€{stats.pendingRevenue.toFixed(2)}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">⏳ Pending</div>
            </div>
          </div>

          {/* Filters */}
          <div className="card p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                label="Search Invoices"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by customer or invoice number..."
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Payment Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="input"
                >
                  <option value="all">All Statuses</option>
                  <option value="paid">✅ Paid</option>
                  <option value="unpaid">⏳ Unpaid</option>
                  <option value="cancelled">🚫 Cancelled</option>
                </select>
              </div>

              <div className="flex items-end">
                <Button onClick={() => exportInvoicesCSV()} variant="secondary" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Invoices Table */}
          <div className="card p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className={invoice.payment_status === 'cancelled' ? 'opacity-60' : ''}>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {invoice.invoice_number}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </div>
                          {invoice.email_sent_at && (
                            <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center mt-1">
                              <Mail className="h-3 w-3 mr-1" />
                              Email sent
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {invoice.customer_name}
                          </div>
                          {invoice.customer_email && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {invoice.customer_email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          €{invoice.total_amount.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Due: {new Date(invoice.due_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">
                            {getPaymentMethodIcon(invoice.payment_method)}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                            {invoice.payment_method}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {getStatusIcon(invoice.payment_status)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.payment_status)}`}>
                            {invoice.payment_status === 'paid' ? '✅ Paid' :
                             invoice.payment_status === 'cancelled' ? '🚫 Cancelled' : '⏳ Unpaid'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setSelectedInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            title="View Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {invoice.customer_email && (
                            <button
                              onClick={() => {
                                setEmailInvoice(invoice);
                                setShowEmailModal(true);
                              }}
                              className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              title="Email Invoice"
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                          )}
                          
                          {invoice.payment_status !== 'cancelled' && (
                            <>
                              {invoice.payment_status === 'unpaid' && (
                                <button
                                  onClick={() => handleStatusUpdate(invoice.id, 'paid')}
                                  className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                  title="Mark as Paid"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              )}
                              
                              <button
                                onClick={() => handleCancelInvoice(invoice.id)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                title="Cancel Invoice"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'reports' && (
        <SalesReports invoices={invoices} />
      )}

      {/* Invoice Viewer Modal */}
      {selectedInvoice && (
        <InvoiceGenerator
          invoice={selectedInvoice}
          user={user!}
          onClose={() => setSelectedInvoice(null)}
          onEmailSent={() => {
            // Refresh invoices to show email sent status
            console.log('Invoice email sent, refreshing data...');
          }}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && emailInvoice && (
        <EmailInvoice
          invoice={emailInvoice}
          onClose={() => {
            setShowEmailModal(false);
            setEmailInvoice(null);
          }}
          onSend={handleEmailInvoice}
        />
      )}
    </div>
  );
};